import { FC, useMemo, useState } from 'react'
import { Box, Checkbox, ListItemText, Select, MenuItem, OutlinedInput, SelectProps, Typography } from '@mui/material'

/* Utils */
import useSearchLabel from 'hooks/useSearchLabel'

/* Components */
import { InstanceCheckedIcon, InstanceUncheckedIcon, SelectDropdownIcon } from 'ui/components/svg'
import theme from 'theme'
import { CategoryMenuProps, IntegrationsFormControl, Wrapper } from './integrationsSelectCustom.styles'
import { IntegrationsSelectResetSearch, IntegrationsSelectSearch } from '.'

interface Props extends SelectProps {
  options: string[]
  value: any
  handleChange: any
  label?: string
  handleReset: any
  hideSearch?: boolean
  field?: string
  maxHeight?: string
  shouldCapitalize?: boolean
}

const IntegrationsSelectField: FC<Props> = ({
  options,
  handleChange,
  placeholder,
  value,
  label,
  handleReset,
  hideSearch = false,
  field,
  maxHeight,
  shouldCapitalize,
  ...rest
}) => {
  const [anchorEl, setAnchorEl] = useState(null)

  const convertedOptions = useMemo(() => {
    return options.map((item) => {
      return {
        label: item,
        name: item
      }
    })
  }, [options])

  const { search, setSearch, labelOptions, resetData, highlightMatch } = useSearchLabel(convertedOptions, handleReset)

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  return (
    <Wrapper className="dropdown-container">
      {label && <Typography className="label-text">{label}</Typography>}
      <IntegrationsFormControl
        isselected={placeholder !== 'Sort' && value.length > 0 ? 'true' : 'false'}
        isopen={anchorEl ? 'true' : 'false'}
      >
        <Select
          labelId="integrations-select-label"
          id="integrations-select"
          displayEmpty
          MenuProps={{
            ...CategoryMenuProps,
            PaperProps: {
              ...CategoryMenuProps.PaperProps,
              sx: {
                ...CategoryMenuProps.PaperProps.sx,
                display: anchorEl ? 'block' : 'none',
                '.list-wrapper': {}
              },
              style: {
                ...CategoryMenuProps.PaperProps.style,
                maxHeight: maxHeight ?? CategoryMenuProps.PaperProps.style.maxHeight
              }
            },
            anchorEl,
            open: Boolean(anchorEl),
            onClose: () => setAnchorEl(null)
          }}
          sx={{
            '& .MuiSelect-icon': { transform: anchorEl ? 'rotate(180deg)' : 'rotate(0deg)' },
            textTransform: shouldCapitalize ? 'capitalize' : ''
          }}
          IconComponent={(props) => (
            <SelectDropdownIcon {...props} className={`${props.className} ${anchorEl ? 'is-open' : ''}`} />
          )}
          renderValue={(selected) => {
            if (Array.isArray(selected)) {
              const pruned = selected.filter((s: string) => s !== '')
              if (pruned.length === 0) {
                return 'None'
              }
              if (pruned.length === 1) {
                return pruned[0]
              }

              if (options.length === value.length) {
                return `All (${pruned.length})`
              }
              return `${pruned.length} selected`
            }

            return selected
          }}
          inputProps={{ 'aria-label': 'Without label' }}
          {...rest}
          value={value}
          onChange={handleChange}
          input={<OutlinedInput onMouseDown={handleClick} />}
        >
          {!hideSearch && <IntegrationsSelectSearch search={search} setSearch={setSearch} placeholder="Search" />}

          <Box className="list-wrapper">
            <MenuItem
              key="all"
              value="All"
              onClick={() => {
                handleChange({ target: { value: value.length === options.length ? [] : options } }, field || '')
                if (!rest.multiple) {
                  setAnchorEl(null)
                }
              }}
            >
              {rest.multiple && (
                <Checkbox
                  checked={value.length === options.length}
                  icon={<InstanceUncheckedIcon />}
                  checkedIcon={<InstanceCheckedIcon />}
                />
              )}
              <ListItemText
                primary={`All (${options.length})`}
                sx={{
                  '.MuiTypography-root': {
                    color: value.length === options.length ? `${theme.colors.menuItemText} !important` : ''
                  }
                }}
              />
            </MenuItem>

            {labelOptions.map((option) => (
              <MenuItem
                key={option.name}
                value={option.name}
                onClick={() => {
                  let nextValue = [...value]
                  if (nextValue.includes(option.name)) {
                    nextValue = nextValue.filter((c) => c !== option.name)
                  } else {
                    nextValue = [...nextValue, option.name]
                  }
                  handleChange({ target: { value: nextValue } }, field || '')
                  if (!rest.multiple) {
                    setAnchorEl(null)
                  }
                }}
              >
                {rest.multiple && (
                  <Checkbox
                    checked={value.indexOf(option.name) > -1}
                    icon={<InstanceUncheckedIcon />}
                    checkedIcon={<InstanceCheckedIcon />}
                  />
                )}
                <ListItemText
                  primary={highlightMatch(option.label)}
                  sx={{
                    '.MuiTypography-root': {
                      color: value.indexOf(option.name) > -1 ? `${theme.colors.menuItemText} !important` : '',
                      textTransform: shouldCapitalize ? 'capitalize' : ''
                    }
                  }}
                />
              </MenuItem>
            ))}
          </Box>

          <IntegrationsSelectResetSearch resetData={resetData} />
        </Select>
      </IntegrationsFormControl>
    </Wrapper>
  )
}

export default IntegrationsSelectField
